import { css, SerializedStyles } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, UseTooltipProps, useTooltip } from '@frontend/design-system';

export interface InfoPopoverItem {
  label: string;
  description: string;
}

export interface AnalyticsInfoPopoverProps {
  title?: string;
  description?: string;
  items?: InfoPopoverItem[];
  placement?: UseTooltipProps['placement'];
  showArrow?: boolean;
  children?: React.ReactNode;
  popoverCss?: SerializedStyles;
}

export const AnalyticsInfoPopover = ({
  title,
  description,
  items = [],
  placement = 'bottom-start',
  showArrow = false,
  children,
  popoverCss,
}: AnalyticsInfoPopoverProps) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement,
    theme: 'light',
    showArrow,
  });

  return (
    <span {...triggerProps} css={styles.infoTrigger}>
      <Icon color='subdued' name='info-small' style={{ minWidth: theme.spacing(2) }} />
      <Tooltip {...tooltipProps} css={[styles.infoPopover, popoverCss]}>
        {title && (
          <Text weight='bold' size='large' css={styles.infoPopoverHeading}>
            {title}
          </Text>
        )}
        {description && (
          <Text weight='light' size='inherit'>
            {description}
          </Text>
        )}
        {items.length > 0 && (
          <ul css={styles.infoPopoverList}>
            {items.map((item) => (
              <li key={item.label}>
                <Text as='span' weight='bold' size='inherit'>
                  {item.label}
                </Text>{' '}
                <Text as='span' size='inherit'>
                  {item.description}
                </Text>
              </li>
            ))}
          </ul>
        )}
        {children}
      </Tooltip>
    </span>
  );
};

const styles = {
  infoTrigger: css`
    display: inline-flex;
    align-items: center;
  `,
  infoPopover: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    max-width: 215px;
    padding: ${theme.spacing(2)};
    text-align: left;
    font-weight: normal;
    font-size: ${theme.fontSize(14)};
    border-radius: ${theme.spacing(1)};
  `,
  infoPopoverHeading: css`
    margin: 0;
  `,
  infoPopoverList: css`
    margin: 0;
    padding-left: ${theme.spacing(2.5)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    list-style-type: disc;
  `,
};
